/**
 * R2 + R23 (2026-10-05) — Settings → Operations: the shop's ONLINE PAYMENTS
 * MODE, read-only, with one line saying what it means for a customer.
 *
 *   off        — "Pay at pickup": customers book online, the desk takes
 *                payment. The platform default (fail closed).
 *   live       — customers pay by card when they book.
 *   simulation — the sales-demo tenant only: checkout completes, no card is
 *                charged.
 *
 * Read-only on purpose: switching a real shop to live card payments is a
 * platform step this release (it must not happen before the gateway has run
 * a real charge — readiness B4). The server's PUT does not accept the field.
 *
 * Design Philosophy: #1 (one fact, stated once, in the owner's words — no
 * enum strings, Principle anti-pattern "internal identifiers shown raw"),
 * #5 (a defaulted value is labelled as the platform default).
 */
import { Badge } from '@ferro/ui';
import type { TenantOnlinePayments } from '../../lib/api.js';

export function OnlinePaymentsModeNote({ onlinePayments }: { onlinePayments: TenantOnlinePayments }): JSX.Element {
  const copy = modeCopy(onlinePayments.effective);
  return (
    <dl className="ferro-settings__readonly" data-testid="operations-online-payments">
      <div className="ferro-settings__readonly-row">
        <dt>Online payments</dt>
        <dd>
          <span data-testid="operations-online-payments-value">{copy.label}</span>{' '}
          {onlinePayments.source === 'default' ? (
            <span data-testid="operations-online-payments-default">
              <Badge variant="platform">Platform default</Badge>
            </span>
          ) : null}
          <span className="ferro-muted ferro-settings__readonly-hint" data-testid="operations-online-payments-hint">
            {copy.hint}
          </span>
        </dd>
      </div>
    </dl>
  );
}

function modeCopy(mode: TenantOnlinePayments['effective']): { label: string; hint: string } {
  switch (mode) {
    case 'off':
      return {
        label: 'Pay at pickup',
        hint: 'Customers book online and pay at the front desk — no card is taken online. Turning on online card payments is a platform step: ask Animal Lovers App support.',
      };
    case 'live':
      return {
        label: 'On — customers pay by card when they book',
        hint: 'Card payments run through your payment processor. Changing this is a platform step: ask Animal Lovers App support.',
      };
    case 'simulation':
      return {
        label: 'Demo — no real charges',
        hint: 'This is a demo business: checkout completes without charging a card.',
      };
    default: {
      const exhaustive: never = mode;
      return exhaustive;
    }
  }
}
